import { useState } from "react";
import Swal from "sweetalert2";
import {
  exportHistoricToExcel,
  exportHistoricToPdf,
} from "../utils/exportHistoric";
import type { ReportTableColumn } from "./ReportPointsTable";

// Metadata for the exported file. `fileBase` is the name WITHOUT extension; the
// component appends `.xlsx` / `.pdf` per format.
export interface ExportMetaInput {
  title: string;
  period: string;
  fileBase: string;
}

interface ExportButtonsProps<T extends { id: string }> {
  rows: T[];
  columns: ReportTableColumn<T>[];
  meta: ExportMetaInput;
  // Extra disable condition on top of "no rows" / "already generating".
  disabled?: boolean;
  // Override the failure handling. When omitted, a default error dialog is
  // shown so a failed export is never silent.
  onError?: (err: unknown) => void;
  className?: string;
}

// Excel + PDF download pair, reusing the reports' `exportHistoric` utility so the
// styling/paging/chunking logic lives in one place. Buttons disable themselves
// when there is nothing to export or while a file is being generated. The whole
// currently-filtered `rows` set is exported — not just the visible page.
export default function ExportButtons<T extends { id: string }>({
  rows,
  columns,
  meta,
  disabled = false,
  onError,
  className = "",
}: ExportButtonsProps<T>) {
  const [exporting, setExporting] = useState<"excel" | "pdf" | null>(null);
  const noData = rows.length === 0;
  const blocked = disabled || noData || exporting !== null;

  const onDownload = async (type: "excel" | "pdf") => {
    if (noData || exporting !== null) return;
    setExporting(type);
    // Yield a tick so the spinner paints before the (potentially heavy) build.
    await new Promise((r) => setTimeout(r, 50));
    try {
      const fullMeta = {
        title: meta.title,
        period: meta.period,
        fileName: `${meta.fileBase}.${type === "excel" ? "xlsx" : "pdf"}`,
      };
      if (type === "excel") {
        await exportHistoricToExcel(rows, columns, fullMeta);
      } else {
        exportHistoricToPdf(rows, columns, fullMeta);
      }
    } catch (err) {
      console.error("[ExportButtons] export failed", err);
      if (onError) {
        onError(err);
      } else {
        Swal.fire({
          icon: "error",
          title: "No se pudo generar el archivo",
          text:
            err instanceof Error
              ? err.message
              : "Ocurrió un error al exportar los datos. Inténtalo de nuevo.",
        });
      }
    } finally {
      setExporting(null);
    }
  };

  return (
    <div className={`inline-flex items-center gap-2 ${className}`}>
      <button
        type="button"
        onClick={() => onDownload("excel")}
        disabled={blocked}
        title="Descargar Excel (datos filtrados)"
        className="inline-flex items-center gap-2 rounded-lg border border-green-500/40 bg-green-500/10 px-3 py-1.5 text-sm font-medium text-green-500 hover:bg-green-500/20 transition-colors disabled:opacity-40 disabled:pointer-events-none"
      >
        <i
          className={
            exporting === "excel"
              ? "ri-loader-4-line animate-spin"
              : "ri-file-excel-2-line"
          }
        />
        <span className="hidden sm:inline">
          {exporting === "excel" ? "Generando…" : "Excel"}
        </span>
      </button>
      <button
        type="button"
        onClick={() => onDownload("pdf")}
        disabled={blocked}
        title="Descargar PDF (datos filtrados)"
        className="inline-flex items-center gap-2 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-1.5 text-sm font-medium text-red-500 hover:bg-red-500/20 transition-colors disabled:opacity-40 disabled:pointer-events-none"
      >
        <i
          className={
            exporting === "pdf"
              ? "ri-loader-4-line animate-spin"
              : "ri-file-pdf-2-line"
          }
        />
        <span className="hidden sm:inline">
          {exporting === "pdf" ? "Generando…" : "PDF"}
        </span>
      </button>
    </div>
  );
}
